import { FC, ReactText } from 'react';
import { useRecoilValue } from 'recoil';
import { Cell, Row as RowProps } from 'react-table';
import { SIZE } from 'common/constants';
import Table from 'components/common/Table/Table';
import { ExportJobTable, ExportJobTableData } from 'components/bulkExports/types';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { timestampToDate } from '../utils';
import ExportStatus from './ExportStatus';

/**
 * Bulk exports table component which shows all data of job exports.
 * @param {string}     title           Table title.
 * @param {Object}     data            Table's cells data.
 * @param {boolean}    loading         Boolean to show loading status when table data loads.
 * @param {Function}   onRowClick      To handle click on table row.
 */
interface BulkExportsTableProps {
  title: string;
  data: ExportJobTableData[];
  loading?: boolean;
  onRowClick: (item: ExportJobTableData) => void;
}

const BulkExportsTable: FC<BulkExportsTableProps> = ({ title, data, onRowClick }: BulkExportsTableProps) => {
  const timezone = useRecoilValue(timezoneState);
  const handleTableRowClick = (item: ExportJobTableData): void => {
    onRowClick(item);
  };

  const headers = [
    { content: 'Requested On', field: 'createdTimestamp', renderCell: ({ value }: { value: ReactText }): string => timestampToDate(Number(value), timezone) },
    { content: 'Label / Query', field: 'name' },
    // TODO: figure out if we want this column to be sorted and how
    { content: 'Time Range', field: 'time' },
    { content: 'Requested By', field: 'createdBy' },
    {
      content: 'Status',
      field: 'status',
      renderCell: ({ value, row }: Cell<ExportJobTableData>): JSX.Element => (
        <ExportStatus status={String(value)} completionPercent={row.original.completionPercent} />
      ),
    },
  ];

  const tableOptions = {
    isStriped: true,
    isSortable: true,
    hoverRow: false,
    headerItemClass: 'BulkExports__TableHeaderItem',
    rowClass: 'BulkExports__TableRow',
    sidePadding: 10,
    size: SIZE.LARGE,
    // Applying initial sorting by date created
    initialState: {
      sortBy: [{ id: 'createdTimestamp', desc: true }],
    },
  };

  const tableItems = data.map((item: ExportJobTableData): ExportJobTable => ({
    ...item,
    time: `${item.timeRangeStart} - ${item.timeRangeEnd}`,
    name: `${item.label || item.query}`,
  }));

  return (
    <>
      <h4 className="SectionHeader">{title}</h4>
      <Table
        headers={headers}
        data={tableItems}
        options={tableOptions}
        userRowProp={(row: RowProps<ExportJobTableData>): {onClick: () => void} => {
          const rowItem = row.original;
          return {
            onClick: (): void => handleTableRowClick(rowItem),
          };
        }}
      />
    </>
  );
};

export default BulkExportsTable;
